<script setup lang="ts">
  import { computed, toRef } from "vue";
  import { DataTable, type DataTableColumn } from "@/components/episto-ui/data-table";
  import { IndexTableEmpty } from "@/components/episto-ui/index-table";
  import { Tooltip } from "@/components/ui/tooltip";
  import { CARD_ICONS } from "@utils/constants/cardCatalog";
  import { useCompletionCards, type CardMetrics, type CompletionCard } from "./completionMetrics";

  const props = defineProps<{ cards: CompletionCard[]; starts: number }>();

  const ROWS = 10;

  const metrics = useCompletionCards(toRef(props, "cards"), toRef(props, "starts"));

  const rows = computed(() =>
    metrics.value
      .filter((card) => card.retention < 1)
      .sort((a, b) => a.retention - b.retention)
      .slice(0, ROWS)
      .map((card, index) => ({ ...card, rank: index + 1 })),
  );

  const columns: DataTableColumn<CardMetrics & { rank: number }>[] = [
    { key: "rank", label: "#", width: 48 },
    { key: "card", label: "Card" },
    { key: "drop", label: "Drop", width: 110, align: "right" },
    { key: "screenout", label: "Screenout", width: 110, align: "right" },
  ];
</script>

<template>
  <div>
    <h2 class="heading-section">Biggest losses</h2>

    <div class="-mx-8 mt-4">
      <DataTable
        :items="rows"
        :columns="columns"
        item-key="token"
        variant="dense"
        fixed-widths
        empty-message="No card loses any respondent.">
        <template #cell-rank="{ item }">
          <span class="text-foreground-subtle tabular-nums">{{ item.rank }}</span>
        </template>
        <template #cell-card="{ item }">
          <div class="flex min-w-0 items-center gap-2">
            <Tooltip :content="item.type">
              <component
                :is="CARD_ICONS[item.cardType] ?? CARD_ICONS.unknown"
                class="size-3.5 shrink-0 text-muted-foreground" />
            </Tooltip>
            <span class="shrink-0 font-medium text-primary">{{ item.name }}</span>
            <span
              class="truncate text-foreground-secondary"
              :title="item.title">
              {{ item.title }}
            </span>
          </div>
        </template>
        <template #cell-drop="{ item }">
          <Tooltip
            v-if="item.nDrop > 0"
            :content="`${item.nDrop} of the ${item.n} respondents who saw the card`">
            <span class="tabular-nums">{{ item.dropOfCard }}%</span>
          </Tooltip>
          <IndexTableEmpty v-else />
        </template>
        <template #cell-screenout="{ item }">
          <Tooltip
            v-if="item.nScreenout > 0"
            :content="`${item.nScreenout} of the ${item.n} respondents who saw the card`">
            <span class="tabular-nums text-status-error-strong">{{ item.screenoutOfCard }}%</span>
          </Tooltip>
          <IndexTableEmpty v-else />
        </template>
      </DataTable>
    </div>
  </div>
</template>
